"use client";

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import { useAuth } from "@/lib/auth/AuthContext";
import { SignUpAbortedError } from "@/lib/auth/AuthProvider";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { getAuthErrorMessage } from "@/lib/auth/authErrorMessage";
import { useTenantSettings, useFeatureFlag } from "@/lib/tenant/TenantSettingsProvider";
import { ConsentCheckboxList } from "@/features/profile-consent/ConsentCheckboxList";
import { consentsForSurface, type ConsentDef } from "@/features/profile-consent/types";
import { submitConsents, richTextToPlainText } from "@/features/profile-consent/profile-consent-utils";

interface SignUpPageProps {
    locale: string;
}

/**
 * Sign-up page component.
 */
export function SignUpPage({ locale }: SignUpPageProps) {
    const router = useRouter();
    const labels = useLabels();
    const tenantSettings = useTenantSettings();
    const { isMultiLang } = tenantSettings;
    const isAuthFeatureEnabled = useFeatureFlag("authentication");
    const { signUp, signInWithGoogle, signOut, user, loading, isAuthEnabled } = useAuth();
    const sdk = useFansUnitedSDK();

    const signupConsents = useMemo(
        () => consentsForSurface(tenantSettings.consents, "signup"),
        [tenantSettings.consents]
    );
    const requiredConsentIds = useMemo(
        () => signupConsents.filter((c) => c.required).map((c) => c.consentId),
        [signupConsents]
    );

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [error, setError] = useState("");
    const [acceptedConsentIds, setAcceptedConsentIds] = useState<string[]>([]);

    const allRequiredAccepted = requiredConsentIds.every((id) => acceptedConsentIds.includes(id));

    // Ref to the email input — used to reset focus on "use-different-email" collision choice.
    const emailInputRef = useRef<HTMLInputElement>(null);

    // While a sign-up flow is in flight (after the Firebase user is created but
    // before consent submission completes), suppress the auto-redirect so we can
    // either roll back on consent failure or navigate explicitly on success.
    const signUpInFlightRef = useRef(false);

    const buildSubmissions = useCallback(
        (ids: string[]) =>
            ids.map((id) => {
                const def: ConsentDef | undefined = signupConsents.find((c) => c.consentId === id);
                return {
                    consentId: id,
                    content: def ? richTextToPlainText(def.body) : null,
                };
            }),
        [signupConsents]
    );

    const t = labels.auth?.signUp || {};
    // Only prefix URLs with locale if tenant has multiple languages
    const basePath = isMultiLang && locale ? `/${locale}` : "";

    // Redirect if already authenticated, but not during an in-flight sign-up
    // (the handler navigates explicitly on success).
    useEffect(() => {
        if (!loading && user && !user.isAnonymous && !signUpInFlightRef.current) {
            router.push(basePath || "/");
        }
    }, [user, loading, router, basePath]);

    if (!isAuthFeatureEnabled || !isAuthEnabled) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4">
                <div className="text-center text-muted-foreground">Authentication is not configured for this site.</div>
            </div>
        );
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError("");

        if (formData.password !== formData.confirmPassword) {
            setError(t.passwordMismatch || "Passwords do not match");
            setIsSubmitting(false);
            return;
        }

        if (formData.password.length < 6) {
            setError(t.passwordTooShort || "Password must be at least 6 characters");
            setIsSubmitting(false);
            return;
        }

        signUpInFlightRef.current = true;
        try {
            await signUp(formData.email, formData.password, formData.name);

            // Set the name on the FansUnited profile
            // The auth token was eagerly set by signUp, so the SDK should be authenticated
            if (sdk && formData.name) {
                try {
                    await sdk.profile.getOwn().setName(formData.name).update();
                } catch (sdkErr) {
                    // Non-blocking — Firebase account is already created
                    console.warn("Failed to set name on FansUnited profile:", sdkErr);
                }
            }

            // Submit accepted consents. Blocking: if the backend rejects the consents,
            // we roll back the just-created Firebase session so the user is not left
            // half-registered without consent on record.
            if (sdk && acceptedConsentIds.length > 0) {
                try {
                    await submitConsents(sdk, buildSubmissions(acceptedConsentIds));
                } catch (consentErr) {
                    console.warn("Consent submission failed after sign-up:", consentErr);
                    try {
                        await signOut();
                    } catch {
                        // Ignore — error is already being surfaced.
                    }
                    throw consentErr;
                }
            }

            signUpInFlightRef.current = false;
            router.push(basePath || "/");
        } catch (err: unknown) {
            signUpInFlightRef.current = false;
            if (err instanceof SignUpAbortedError) {
                if (err.reason === "use-different-email") {
                    setFormData((prev) => ({ ...prev, email: "" }));
                    emailInputRef.current?.focus();
                }
                // "keep-guest" → do nothing, dialog already closed, user stays on page
                setIsSubmitting(false);
                return;
            }
            setError(getAuthErrorMessage(err, labels) ?? "");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError("");
        setIsGoogleLoading(true);
        signUpInFlightRef.current = true;
        try {
            await signInWithGoogle();

            // Submit accepted consents after Google sign-in resolves successfully.
            // Blocking: roll back the session if the backend rejects the consents.
            if (sdk && acceptedConsentIds.length > 0) {
                try {
                    await submitConsents(sdk, buildSubmissions(acceptedConsentIds));
                } catch (consentErr) {
                    console.warn("Consent submission failed after Google sign-in:", consentErr);
                    try {
                        await signOut();
                    } catch {
                        // Ignore — error is already being surfaced.
                    }
                    throw consentErr;
                }
            }

            signUpInFlightRef.current = false;
            router.push(basePath || "/");
        } catch (err: unknown) {
            signUpInFlightRef.current = false;
            if (err instanceof SignUpAbortedError) {
                // Dialog was dismissed (keep-guest or use-different-email) — reset state silently.
                setIsGoogleLoading(false);
                return;
            }
            setError(getAuthErrorMessage(err, labels) ?? "");
        } finally {
            setIsGoogleLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 bg-background">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-foreground">{t.title || "Create Account"}</h1>
                    <p className="mt-2 text-muted-foreground">{t.subtitle || "Join our community"}</p>
                </div>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1">
                                {t.name || "Full Name"}
                            </label>
                            <input
                                id="name"
                                name="name"
                                type="text"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 input"
                            />
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
                                {t.email || "Email"}
                            </label>
                            <input
                                ref={emailInputRef}
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 input"
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">
                                {t.password || "Password"}
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 input"
                            />
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-1">
                                {t.confirmPassword || "Confirm Password"}
                            </label>
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 input"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="text-red-500 text-sm text-center bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                            {error}
                        </div>
                    )}

                    {signupConsents.length > 0 && (
                        <ConsentCheckboxList consents={signupConsents} onChange={setAcceptedConsentIds} />
                    )}

                    <button
                        type="submit"
                        disabled={isSubmitting || isGoogleLoading || !allRequiredAccepted}
                        className="w-full py-3 px-4 font-medium btn-primary disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
                    >
                        {isSubmitting && (
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                            </svg>
                        )}
                        {isSubmitting
                            ? t.creatingAccount || "Creating account..."
                            : t.createAccountButton || "Create Account"}
                    </button>

                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-background text-muted-foreground">
                                {t.orContinueWith || "Or continue with"}
                            </span>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        disabled={isSubmitting || isGoogleLoading || !allRequiredAccepted}
                        className="w-full py-3 px-4 border border-border rounded-lg font-medium text-foreground hover:bg-muted transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isGoogleLoading ? (
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path
                                    fill="currentColor"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                            </svg>
                        )}
                        {isGoogleLoading
                            ? t.signingInWithGoogle || "Signing in..."
                            : t.continueWithGoogle || "Continue with Google"}
                    </button>
                </form>

                <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                        {t.hasAccount || "Already have an account?"}{" "}
                        <Link
                            prefetch={false}
                            href={`${basePath}/sign-in`}
                            className="text-primary hover:underline cursor-pointer"
                        >
                            {t.signInLink || "Sign in"}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
